import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, CircularProgress, Stack } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { GetDateNumeric } from '../../generic/DateTimeFunctions'
import { apiDownloadFile } from '../../generic/Api_Functions'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
// Import-free leaf module (no three.js): safe to import eagerly even though the viewer itself is
// lazy-loaded below (S70 — code-splitting, historia 56).
import { extensionConversionMap, isExtensionViewable } from '../document/documentExtensions'
import {
  getDocumentRepositoryTopics,
  getDocumentTypes,
  searchDocumentRepository,
  toDocumentRepositorySearchRequest,
  criteriaPatchFromTableRequest,
  documentRepositoryTablePage,
  documentRepositoryPreviewUrl,
  documentRepositoryDownloadUrl,
  deleteDocumentRepositoryDocument
} from './documentRepositoryApi'
import { columnFilterFromCreatedDateRange } from './documentRepositoryDateRange'
import { topicLabelKey, navigationWindowFor } from './documentRepositoryTopics'
import { describeAssociatedRecord, associatedRecordLabel, isOwnPathSegment, joinRecordLabel } from './documentRepositoryRecordCell'
import { DEFAULT_CRITERIA, criteriaFromSearch, searchFromCriteria, hasActiveCriteria } from './documentRepositoryUrlState'
import DocumentRepositoryUploadDialog from './DocumentRepositoryUploadDialog'
import DocumentRepositoryCardDialog from './DocumentRepositoryCardDialog'
import DocumentRepositoryAssociatedRecordFilter from './DocumentRepositoryAssociatedRecordFilter'

// S70 — SmartDocumentViewer drags three.js; loaded only when a row is actually previewed, never on
// mount of the repository screen itself. `SmartDocumentViewer.jsx` has no default export (named
// export only), so the lazy wrapper maps it here.
const LazySmartDocumentViewer = React.lazy(() =>
  import('../document/SmartDocumentViewer').then((module) => ({ default: module.SmartDocumentViewer }))
)

// Historia 170, J1 — one entry per data column. D10: Sun now translates a column's own `field` into
// its sort enum itself (`encargo-sun-r2-request-pagination.md`), so this table no longer needs its own
// field->enum map — `sortable: false` (S166: "Associated record" never orders) is all a column needs
// to declare now.
export const DOCUMENT_REPOSITORY_COLUMNS = [
  { field: 'documentName', headerKey: 'Document name', sortable: true },
  { field: 'fileName', headerKey: 'File name', sortable: true },
  { field: 'description', headerKey: 'Description', sortable: false },
  { field: 'documentType.name', headerKey: 'Document type', sortable: true },
  { field: 'extension', headerKey: 'Extension', sortable: true },
  { field: 'topic', headerKey: 'Topic', sortable: true },
  { field: 'record', headerKey: 'Associated record', sortable: false },
  { field: 'createdUser', headerKey: 'Uploaded by', sortable: true },
  { field: 'createdDate', headerKey: 'Upload date', sortable: true },
  { field: 'updatedUser', headerKey: 'Last updated user', sortable: false },
  { field: 'updatedDate', headerKey: 'Last update date', sortable: false }
]

/** RN6 (S23) — a WORKFLOW ancestor renders code + version + template/OF; anything else is code — name. */
const PathSegment = ({ segment, t_ }) => {
  if (segment.topic === 'WORKFLOW') {
    const templateLabel = t_(segment.linkedToFabricationOrder ? 'Fabrication order workflow' : 'Template')
    return <span>{segment.code}{segment.version ? ' · ' + segment.version : ''} · {templateLabel}</span>
  }
  return <span>{joinRecordLabel(segment.code, segment.name)}</span>
}

const AssociatedRecordCell = ({ row, t_ }) => {
  const descriptor = describeAssociatedRecord(row)
  if (descriptor.kind === 'taskCount') {
    return <span>{t_('{{count}} tasks', { count: descriptor.count })}</span>
  }
  return (
    <Stack spacing={0.5}>
      <span>{associatedRecordLabel(row)}</span>
      {descriptor.path.filter((segment) => !isOwnPathSegment(row, segment)).map((segment, index) => (
        <PathSegment key={segment.topic + ':' + segment.referenceId + ':' + index} segment={segment} t_={t_} />
      ))}
    </Stack>
  )
}

const DocumentRepository = () => {
  const { t_ } = useSmartTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const isUserAuthorized = useAuthorization()

  // `criteria` stays the single source of truth for the URL (CA4.3). Refactor (2nd round):
  // `DocumentRepositoryFilters.jsx` is GONE — every field here (text/page/size/sort AND every column
  // filter) is resolved from the table's OWN `requestPagination` (general search box + per-column
  // filters) and synced back on every fetch (`dataFetch` below) purely so the URL keeps reflecting it
  // (S94/S95); nothing here reacts to those fields changing (that would defeat the whole point of
  // letting the table own them). D10 (3rd round): the 5 substring filters and the topic filter now
  // store their RAW TYPED TEXT (`documentRepositoryUrlState.js`'s own header comment) — Sun resolves
  // type/extension/uploaded-by itself now, and the topic literal is only resolved at the API boundary
  // (`documentRepositoryApi.js`'s `toDocumentRepositorySearchRequest`), never here.
  // `recordTopic`/`recordReferenceId` (3rd refactor round, S48 restored) are the ONE exception: no
  // column-filter primitive fits their two-step topic-then-text search, so they stay SCREEN-owned,
  // set/cleared via `DocumentRepositoryAssociatedRecordFilter` in the table's own toolbar (see its own
  // comment below).
  const [criteria, setCriteria] = useState(() => criteriaFromSearch(location.search))
  const criteriaRef = useRef(criteria)
  criteriaRef.current = criteria
  // Best-effort display label for the record filter above: there is no "fetch one record by id"
  // endpoint (only R3's own search), so a record restored from the URL (S94/S95) starts with a
  // generic `topic — referenceId` fallback until the user reopens the picker and re-selects, at which
  // point the real `code — name` from R3 takes over (`handleRecordFilterSelected` below).
  const [recordLabel, setRecordLabel] = useState(() => {
    const seeded = criteriaFromSearch(location.search)
    return seeded.recordReferenceId ? t_(topicLabelKey(seeded.recordTopic)) + ' — ' + seeded.recordReferenceId : ''
  })
  const [topics, setTopics] = useState([])
  const [documentTypes, setDocumentTypes] = useState([])
  // D10 (4th round): the ONLY catalogue any column filter still needs to resolve against is R1's own
  // `topics` — the Topic filter's typed label must translate into `DocumentTopic` literals for Sun's
  // `mixedColumnFilters` (RN31), and mounting the table before R1 resolves would risk its very first
  // request silently losing whatever topic the URL restored (S94/S95), since there would be nothing yet
  // to match against. Type/extension/uploaded-by no longer need ANY catalogue on this side (Sun resolves
  // them by substring itself now) — `documentTypes` stays fetched (still needed by the upload/edit
  // dialogs' own dropdown, unrelated to this gate) but no longer gates the table's mount.
  const [hasFetchedTopics, setHasFetchedTopics] = useState(false)
  // S53 (CA2.5, GATE 3 bandera B3) — "Clear filters" is offered ONLY in the no-results state (an
  // active criterion that matched nothing), never with rows on screen (`encargo-jupiter-clear-
  // filters.md`, reverting the 5th round's "always visible with active criteria"). This is the
  // MINIMAL state needed to know that: `totalElements` (the last response's own total, `0` default —
  // never `null`/`undefined`, CLAUDE.md) and `hasFetchedOnce` (`false` until the very first response
  // settles, so the button never flashes visible before the screen even knows there IS a criterion
  // with zero matches).
  const [totalElements, setTotalElements] = useState(0)
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)
  // S52 (CA6.3, G2-8) — the table's OWN column filters already reset ITS server page to 0 by
  // themselves on every change (`changeColumnFilter`/`applyColumnFilter`/`removeColumnFilter`,
  // `generic/SmartMateriaUITable.jsx`); this token is only needed for the ONE thing nothing else
  // resets: after a successful upload/edit/delete (S76/S81/S82), landing back on page 0 without
  // disturbing whatever the user currently has typed/sorted/filtered (`resetPageable`, a dedicated
  // additive token — see its own comment in `SmartMateriaUITable.jsx` for why `resetPage` itself
  // cannot be reused for a `pageable` table).
  const [refreshToken, setRefreshToken] = useState(0)
  // "Clear filters" (S53) is the only action that must ALSO forget the table's own state (general
  // search text, current sort, AND now every column filter) — bumping this `key` remounts
  // `SmartMateriaUITable` from scratch, so `initialFilterText`/`initialColumnFilters`/
  // `initialColumnFilterOperators`/`sortOptions` re-seed from the just-reset `criteria` (its own
  // defaults). There is no other way to reach into the table's own `columnFilters` ref from outside.
  const [tableRemountKey, setTableRemountKey] = useState(0)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  // "no row" default is `{}` (never `null`/`undefined`, CLAUDE.md): a real row always has a
  // `documentId`, so `row.documentId` alone is a safe truthiness check for "is a dialog open".
  const [editingRow, setEditingRow] = useState({})
  const [deletingRow, setDeletingRow] = useState({})
  const [blockedDeleteRow, setBlockedDeleteRow] = useState({})
  const [previewRow, setPreviewRow] = useState({})
  const [AlertElement, showAlert] = AlertUI()

  const showServerError = (literal) => showAlert({ title: '', message: t_(literal), severity: 'error' })

  const translateTopicLabel = useCallback((code) => t_(topicLabelKey(code)), [t_])

  // R1 — loaded once: the topic catalogue (and its facets, RN31) does not depend on the search
  // criteria (CU1 step 2). D10 (4th round): `extensions`/`createdUsers` are no longer read here — Sun
  // resolves those two filters by substring itself now, Jupiter never needs their catalogue at all.
  useEffect(() => {
    let cancelled = false
    getDocumentRepositoryTopics().then((response) => {
      if (cancelled) return
      setTopics((response && response.topics) || [])
      setHasFetchedTopics(true)
    }).catch(() => {
      if (cancelled) return
      setTopics([])
      setHasFetchedTopics(true)
    })
    return () => { cancelled = true }
  }, [])

  // J2 (S49) — reused existing catalogue (impacto.md §Jupiter), loaded once. D10 (4th round): only the
  // upload/edit dialogs' own dropdown still needs this — it no longer gates the table's own mount (see
  // `hasFetchedTopics`'s own header comment above).
  useEffect(() => {
    let cancelled = false
    getDocumentTypes().then((response) => {
      if (!cancelled) setDocumentTypes(response || [])
    }).catch(() => {
      if (!cancelled) setDocumentTypes([])
    })
    return () => { cancelled = true }
  }, [])

  // Always-latest topic catalogue, read by the STABLE `dataFetch` below (a ref, exactly like
  // `criteriaRef`, so `dataFetch` never needs to change identity just because R1 resolved).
  const catalogsRef = useRef({ topics, translateTopicLabel })
  catalogsRef.current = { topics, translateTopicLabel }

  // R2 — `SmartMateriaUITable`'s own `dataFetch` function (`pageable`): it receives the table's
  // `requestPagination` (page/size/sort/general-search/column-filters, `usePagination.jsx`) and must
  // return `{ content, ...pageable fields }`. `criteriaPatchFromTableRequest`/
  // `toDocumentRepositorySearchRequest`/`documentRepositoryTablePage` (`documentRepositoryApi.js`) are
  // the pure adapters on each side (D10: `requestPagination` travels to R2 almost as-is now, see that
  // file's own header comment). A STABLE function (never changes identity, reading the LATEST
  // criteria/catalogues via refs): the table itself decides WHEN to call it (typing, sorting, paging,
  // filtering, or the `resetPageable` bump below after a successful write) — this never needs to
  // change identity for that, and MUST not (a `dataFetch` reference that flips on every fetch, because
  // the fetch itself syncs `criteria`, would re-trigger the table's own effect forever).
  const dataFetch = useCallback((requestPagination) => {
    const filterCriteria = criteriaRef.current
    const catalogs = catalogsRef.current
    const patch = criteriaPatchFromTableRequest(requestPagination)
    // CA4.3 — keep the query string in sync so "back" restores exactly what the table itself last
    // asked for (S94/S95), even though this screen's own controls never touch these fields anymore.
    setCriteria((previous) => ({ ...previous, ...patch }))
    const recordFilter = { recordTopic: filterCriteria.recordTopic, recordReferenceId: filterCriteria.recordReferenceId }
    return searchDocumentRepository(toDocumentRepositorySearchRequest(requestPagination, recordFilter, catalogs))
      .then((response) => {
        const page = documentRepositoryTablePage(response)
        setTotalElements(page.totalElements)
        setHasFetchedOnce(true)
        return page
      })
      .catch(() => {
        setTotalElements(0)
        setHasFetchedOnce(true)
        return { content: [], totalElements: 0, number: requestPagination.page, size: requestPagination.size }
      })
  }, [])

  useEffect(() => {
    navigate({ pathname: location.pathname, search: searchFromCriteria(criteria) }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria])

  // UX (coordinator): clearing filters forgets the table's own state entirely (general search text,
  // current sort, AND every column filter) by remounting it from scratch — the exact same "forget
  // everything" `DEFAULT_CRITERIA` reset as before this refactor. Also forgets the record filter
  // (screen-owned, not part of the table's own remount) and its display label.
  const clearFilters = () => {
    setCriteria({ ...DEFAULT_CRITERIA })
    setRecordLabel('')
    setTableRemountKey((token) => token + 1)
  }

  // S48 (3rd round) — the record filter's own handlers: `recordTopic`/`recordReferenceId` are
  // screen-owned criteria (see the header comment on `criteria`), so setting/clearing them here is
  // the same "always resets to page 0, never touches the current sort" contract as the OLD
  // `DocumentRepositoryFilters.jsx` (G2-8/RN30, CA6.3) — done via `resetPageable` (`refreshToken`),
  // the same token already used to refresh after a successful write, since the table itself has no
  // way to know this SCREEN-owned field changed (it is not one of its own column filters).
  const handleRecordFilterSelected = (recordTopic, recordReferenceId, record) => {
    setCriteria((previous) => ({ ...previous, recordTopic, recordReferenceId }))
    setRecordLabel(record.code + ' — ' + record.name)
    setRefreshToken((token) => token + 1)
  }
  const handleRecordFilterCleared = () => {
    setCriteria((previous) => ({ ...previous, recordTopic: '', recordReferenceId: '' }))
    setRecordLabel('')
    setRefreshToken((token) => token + 1)
  }

  // S69/S70 (CU3) — preview: view is visible only with `permissions.view` AND a previewable
  // extension (`documentExtensions.js`, the same leaf list the module uses — S69). STEP/STP/DXF get
  // `targetExtension=GLB` in the URL Jupiter builds (Sun does not decide this, the caller does).
  const handlePreview = (row) => setPreviewRow(row)
  const closePreview = () => setPreviewRow({})

  // S72 (CU4) — download: the byte stream is fetched straight from R5's URL, same generic helper
  // every other download in MIC uses.
  const handleDownload = (row) => apiDownloadFile(documentRepositoryDownloadUrl(row.topic, row.referenceId, row.documentId), row.fileName + '.' + row.extension)

  /**
   * S89-S92 (CU8, J4a) — `navigationWindowFor` (03-dominio.md's frozen map) translates
   * `row.navigation.window.key` into a route and the permission that guards it. The menu guard
   * itself (`isCurrentPathAllowed`, `MainMenuPermissionController.jsx:203-210`) is private to that
   * component (not exported, not reusable) — asking `useAuthorization` for the EXACT permission code
   * the frozen map already declares per window is an equivalent check for these 12 known
   * destinations (declared deviation from "pregunta al guard", see plan-jupiter.md), without
   * duplicating the guard's full path-matching machinery here.
   *
   * `selectionReferenceId`/`selectionCode` travel as `''` (never `null`, CLAUDE.md) when there is no
   * child selection — 03-dominio.md's own wording says "o null" for this contract, but that is J4b's
   * concern once a real window reads it; `''` is this screen's own "absent" sentinel, same convention
   * as every other criteria field, and `useRepositorySelection()` (`generic/`, exempt) returns
   * whatever is here verbatim either way.
   */
  const handleOpenAssociatedRecord = (row) => {
    const { window, selection } = row.navigation
    const target = navigationWindowFor(window.key)
    if (!target.path || !isUserAuthorized(target.permissionCode)) {
      showAlert({ title: '', message: t_('You do not have access to this record'), severity: 'warning' })
      return
    }
    if (window.key === 'WORKFLOW') {
      navigate(target.path, {
        state: {
          workflowId: Number(window.referenceId),
          ...(selection ? { jobReferenceId: Number(selection.referenceId) } : {})
        }
      })
      return
    }
    navigate(target.path, {
      state: {
        repositorySelection: {
          windowReferenceId: window.referenceId,
          windowCode: window.code,
          selectionReferenceId: selection ? selection.referenceId : '',
          selectionCode: selection ? selection.code : ''
        }
      }
    })
  }

  // S78/S79 (CU6) — edit: `associationCount` (already on the row, RN23a) drives the warning inside
  // the dialog itself (DocumentRepositoryCardDialog.jsx); this screen just opens/closes it.
  const handleEditClick = (row) => setEditingRow(row)
  // Saving the card always reports its outcome: a success alert, or Sun's own literal (falling back
  // to a generic one when the error carries no message, so the alert is never blank).
  const handleEditSaved = () => {
    setEditingRow({})
    setRefreshToken((token) => token + 1)
    showAlert({ title: '', message: t_('Changes saved successfully'), severity: 'success' })
  }
  const handleEditError = (literal) => showServerError(literal || 'Error saving changes')

  // S81/S174/S175 (CU7) — delete: RN23b/G2-9 splits in two paths BEFORE any confirmation exists —
  // `associationCount > 1` blocks with an info-only dialog (no way to confirm, S174); exactly `1`
  // (or the field absent on an older payload) asks for the normal confirmation (S175/S81).
  const handleDeleteClick = (row) => {
    if (row.associationCount > 1) {
      setBlockedDeleteRow(row)
      return
    }
    setDeletingRow(row)
  }
  const confirmDelete = async () => {
    const target = deletingRow
    setDeletingRow({})
    try {
      await deleteDocumentRepositoryDocument(target.topic, target.referenceId, target.documentId)
      setRefreshToken((token) => token + 1)
    } catch (error) {
      showServerError(error && error.message ? error.message : '')
    }
  }

  // S73 (CU5) — upload: visible only when at least one topic has ADD *and* recordSearch (RN21).
  const uploadableTopics = topics.filter((topic) => topic.permissions.add && topic.recordSearch)
  // S48 (3rd round) — the record filter's own topics (RN27 mirror: only `recordSearch` topics offer
  // records to filter by, same reasoning as "no se ofrece como destino" for uploads above). Shared
  // `DocumentRepositoryRecordPicker` component, now used by BOTH this filter and the upload dialog.
  const recordSearchTopics = topics.filter((topic) => topic.recordSearch)
  const handleUploaded = () => {
    setUploadDialogOpen(false)
    setRefreshToken((token) => token + 1)
  }
  const handleUploadError = (literal) => showServerError(literal)

  // Refactor (2nd round) — Temática/Tipo/Extensión/Subido por/Fecha de subida are now the table's OWN
  // per-column filters (`generic/SmartMateriaUITable.jsx` offers exactly two primitives: a plain
  // free-text substring per column, and — date columns only — `DateColumnFilterPopover`'s
  // operator+instant; neither is a multi-select). `column.filter` seeds a TEXT column's own filter box
  // on mount (S94/S95) — the table's own mechanism for a "static initial value", re-read on every
  // render until the user edits ANY filter. D10 (4th round): every text filter's seed is now simply the
  // RAW TYPED TEXT `criteria` already stores (`documentNameFilter`/`fileNameFilter`/`documentTypeFilter`/
  // `extensionFilter`/`createdUserFilter`/`topicFilter`) — no catalogue lookup needed anymore to
  // re-derive it, Sun resolves the actual matching server-side. Document name/File name get their OWN
  // filter box now too (D10 lets Sun filter them independently — before, S46's general search was the
  // only way to reach either). "Associated record" has no column filter (S48 lives in the table's own
  // toolbar instead, see `DocumentRepositoryAssociatedRecordFilter` below — no column-filter primitive
  // fits its two-step topic-then-text search), keeping only its DATA column.
  const columns = useMemo(() => {
    const seeded = criteriaRef.current
    return [
      { name: t_('Document name'), field: 'documentName', filter: seeded.documentNameFilter },
      { name: t_('File name'), field: 'fileName', filter: seeded.fileNameFilter },
      // Sun filters R2 by description (substring, never split on commas) but does not sort by it.
      { name: t_('Description'), field: 'description', sortable: false, filter: seeded.descriptionFilter },
      {
        name: t_('Document type'),
        field: 'documentType.name',
        filter: seeded.documentTypeFilter,
        render: (row) => (row.documentType ? row.documentType.name : '')
      },
      { name: t_('Extension'), field: 'extension', filter: seeded.extensionFilter },
      {
        name: t_('Topic'),
        field: 'topic',
        filter: seeded.topicFilter,
        render: (row) => t_(topicLabelKey(row.topic))
      },
      {
        name: t_('Associated record'),
        field: 'record',
        sortable: false,
        search: false,
        render: (row) => <AssociatedRecordCell row={row} t_={t_} />
      },
      { name: t_('Uploaded by'), field: 'createdUser', filter: seeded.createdUserFilter },
      {
        name: t_('Upload date'),
        field: 'createdDate',
        type: 'date',
        render: (row) => (row.createdDate ? GetDateNumeric(row.createdDate) : '')
      },
      // Last update of the document card: Sun neither sorts nor filters R2 by these, so they are
      // display-only.
      { name: t_('Last updated user'), field: 'updatedUser', sortable: false, search: false },
      {
        name: t_('Last update date'),
        field: 'updatedDate',
        sortable: false,
        search: false,
        render: (row) => (row.updatedDate ? GetDateNumeric(row.updatedDate) : '')
      }
    ]
    // `tableRemountKey` is a genuine dependency, not just an eslint appeasement: it is what "Clear
    // filters" (S53) bumps, and `SmartMateriaUITable`'s `column.filter` mechanism only re-seeds a
    // column's own filter box from a STATIC value computed HERE, at the PARENT's render — recomputing
    // `columns` exactly when the child remounts is what makes the freshly-reset `criteria` (now
    // `DEFAULT_CRITERIA`) actually reach the brand new table instance, instead of it re-seeding
    // whatever STALE filter text this memo last computed before the clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t_, tableRemountKey])

  // Row actions live in the table's "..." menu (`generic/ActionsMenu.jsx`): each entry hides itself
  // (`hide`) when the row's permissions/extension/navigation do not allow it, and the menu button
  // itself disappears when no entry is left for that row.
  const actions = useMemo(() => ({
    custom: [
      {
        name: 'View',
        hide: (row) => !(row.permissions.view && isExtensionViewable(row.extension)),
        render: () => <VisibilityIcon />,
        onClick: (row) => handlePreview(row)
      },
      {
        name: 'Download',
        hide: (row) => !row.permissions.download,
        render: () => <DownloadIcon />,
        onClick: (row) => handleDownload(row)
      },
      {
        name: 'Edit',
        hide: (row) => !row.permissions.edit,
        render: () => <EditIcon />,
        onClick: (row) => handleEditClick(row)
      },
      {
        name: 'Delete',
        hide: (row) => !row.permissions.delete,
        render: () => <DeleteIcon />,
        onClick: (row) => handleDeleteClick(row)
      },
      {
        name: 'Open associated record',
        hide: (row) => !row.navigation,
        render: () => <OpenInNewIcon />,
        onClick: (row) => handleOpenAssociatedRecord(row)
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t_])

  // Restores the initial sort from the URL (S94/S95): `SmartMateriaUITable`'s own column-header click
  // ALWAYS wins over this (`usePagination.jsx` puts the user's click first, this default second —
  // `documentRepositoryApi.js`'s adapters only ever read index 0). D10 (4th round): `criteria.sortKey`
  // IS the column `field` now (Sun translates it into its own sort enum itself) and
  // `criteria.sortDirection` IS already `1`/`-1` — no lookup map needed anymore on this side. Memoized
  // so its reference is stable across renders that do not actually change the sort criterion (an
  // unstable `sortOptions` would re-trigger the table's own request-building effect on every unrelated
  // render).
  const sortOptions = useMemo(() => ([{
    field: criteria.sortKey,
    direction: criteria.sortDirection
  }]), [criteria.sortKey, criteria.sortDirection])

  // Refactor (2nd round) — seeds the "Upload date" column's `DateColumnFilterPopover` (its own
  // `storedValue`/`storedOperator`, read directly off `SmartMateriaUITable`'s internal ref: unlike a
  // plain text column, it has no `column.filter`-style "static initial value" mechanism of its own).
  // Memoized (like `sortOptions`) so an unstable reference never re-triggers the table's own
  // request-building effect on an unrelated render.
  // `tableRemountKey` (not `criteria.createdFromMillis/ToMillis` directly) is the dependency on
  // purpose: this must behave like `columns`' own `filter` seed above (a ONE-SHOT value re-read only
  // when `SmartMateriaUITable` itself remounts — "Clear filters", S53), never on every ordinary
  // patch-back from the table's own fetches (which would fight the user's own typing, the same reason
  // `initialFilterText`/`initialPage` are one-shot seeds too).
  const initialColumnFilters = useMemo(() => {
    const dateFilter = columnFilterFromCreatedDateRange(criteria.createdFromMillis, criteria.createdToMillis)
    return dateFilter.value ? { createdDate: dateFilter.value } : {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRemountKey])
  const initialColumnFilterOperators = useMemo(() => {
    const dateFilter = columnFilterFromCreatedDateRange(criteria.createdFromMillis, criteria.createdToMillis)
    return dateFilter.operator ? { createdDate: dateFilter.operator } : {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRemountKey])

  const activeCriteria = hasActiveCriteria(criteria)
  // S26/S53 (RN32) — an active criterion that matched nothing at all, once the first response has
  // actually settled (never before: a `false` default keeps the "no results" state from ever being
  // true before the screen has heard back from R2 even once).
  const isEmpty = hasFetchedOnce && totalElements === 0
  // D10 (4th round): only R1's own `topics` still gates the table's mount (Topic filter resolution,
  // RN31) — `documentTypes` no longer needs to (see `hasFetchedTopics`'s own header comment).
  const topicsReady = hasFetchedTopics
  // Refactor (5th round) — the screen no longer paints its OWN empty-state message: it is
  // `SmartMateriaUITable`'s own `noDataMessage` now (an EXISTING, non-additive prop — see its own
  // default `'No data to display'`), which avoids the duplicate-message risk a client-side message
  // ALONGSIDE the table would create. Passed as the untranslated KEY (`noDataMessage` translates it
  // internally via its own `t_(noDataMessage)`, exactly like its own default does) — RN32 (S26) still
  // decides WHICH key: an active criterion with no matches (S53) vs. nothing visible at all (S26).
  const noDataMessageKey = activeCriteria ? 'No document matches the search or the filters' : 'There is no documentation visible for your profile'

  return (
    <Box>
      {AlertElement}
      <Stack direction='row' spacing={2} alignItems='flex-start' justifyContent='flex-end' sx={{ mb: 2 }}>
        {/* S53 (GATE 3 bandera B3, `encargo-jupiter-clear-filters.md`) — "Clear filters" lives in the
            screen's own header (never inside the table itself), but is offered ONLY in the no-results
            state: an active criterion (`activeCriteria`) that matched nothing at all (`isEmpty`), right
            alongside the table's own `noDataMessage`. With rows on screen the button does not appear,
            even with an active criterion — S26 (no criterion at all) already excludes it via
            `activeCriteria` alone. */}
        {activeCriteria && isEmpty && <Button onClick={clearFilters}>{t_('Clear filters')}</Button>}
        {uploadableTopics.length > 0 && (
          <Button variant='contained' onClick={() => setUploadDialogOpen(true)}>{t_('Upload document')}</Button>
        )}
      </Stack>

      {/* S168 — the result count is the table's own paginator total ("1–10 of N"); the separate
          "N documents found" label was removed as redundant (developer decision). */}
      {topicsReady && (
        <SmartMateriaUITable
          key={tableRemountKey}
          tableTuneUniqueKey='documentRepository'
          columns={columns}
          pageable
          dataFetch={dataFetch}
          actions={actions}
          tablePageSize={criteria.size}
          initialPage={criteria.page}
          searchPosition='left'
          sortOptions={sortOptions}
          sortResetsPageableToZero
          resetPageable={refreshToken}
          initialFilterText={criteria.text}
          initialColumnFilters={initialColumnFilters}
          initialColumnFilterOperators={initialColumnFilterOperators}
          noDataMessage={noDataMessageKey}
          toolbar={{
            render: {
              fetchFunction: () => (
                <DocumentRepositoryAssociatedRecordFilter
                  topics={recordSearchTopics}
                  recordReferenceId={criteria.recordReferenceId}
                  recordLabel={recordLabel}
                  onSelect={handleRecordFilterSelected}
                  onClear={handleRecordFilterCleared}
                />
              )
            }
          }}
        />
      )}

      {uploadDialogOpen && (
        <DocumentRepositoryUploadDialog
          uploadableTopics={uploadableTopics}
          documentTypes={documentTypes}
          onClose={() => setUploadDialogOpen(false)}
          onUploaded={handleUploaded}
          onError={handleUploadError}
        />
      )}

      {Boolean(editingRow.documentId) && (
        <DocumentRepositoryCardDialog
          row={editingRow}
          documentTypes={documentTypes}
          onClose={() => setEditingRow({})}
          onSaved={handleEditSaved}
          onError={handleEditError}
        />
      )}

      {Boolean(deletingRow.documentId) && (
        <SmartDialog
          setOpen
          message={t_('Delete this document from this record?')}
          acceptCallback={confirmDelete}
          cancelCallback={() => setDeletingRow({})}
          closeCallback={() => setDeletingRow({})}
        />
      )}

      {/* S174 (RN23b/G2-9) — several associations: an INFO-ONLY dialog, no way to confirm, R8 never
          sent from here (the row stays exactly as it is). */}
      {Boolean(blockedDeleteRow.documentId) && (
        <SmartDialog
          setOpen
          message={t_('This document is in {{count}} records and can not be deleted from here', { count: blockedDeleteRow.associationCount })}
          accept={false}
          cancelCallback={() => setBlockedDeleteRow({})}
          closeCallback={() => setBlockedDeleteRow({})}
        />
      )}

      {Boolean(previewRow.documentId) && (
        <SmartDialog
          setOpen
          growContainerHeight
          paperWidth='60rem'
          paperHeight='40rem'
          accept={false}
          cancelCallback={closePreview}
          closeCallback={closePreview}
          renderComponent={(
            <Suspense fallback={<CircularProgress />}>
              <LazySmartDocumentViewer
                documentUUID={previewRow.documentUuid}
                documentName={previewRow.documentName}
                sensitive={previewRow.sensitive === true}
                extension={previewRow.extension}
                previewUrl={documentRepositoryPreviewUrl(
                  previewRow.topic,
                  previewRow.referenceId,
                  previewRow.documentId,
                  (extensionConversionMap[(previewRow.extension || '').toLowerCase()] || '').toUpperCase()
                )}
              />
            </Suspense>
          )}
        />
      )}
    </Box>
  )
}

export default DocumentRepository
